import { useState } from 'react'
import { Eye, Code, Columns } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import rehypeHighlight from 'rehype-highlight'

interface Props {
  originalText: string
  beautifiedText: string | null
  beautifiedHtml: string | null
}

type ViewMode = 'side-by-side' | 'original' | 'beautified'

export function StudioSideBySide({ originalText, beautifiedText, beautifiedHtml }: Props) {
  const [viewMode, setViewMode] = useState<ViewMode>('side-by-side')
  const [previewMode, setPreviewMode] = useState<'rendered' | 'markdown'>('rendered')

  const VIEW_MODES: { id: ViewMode; label: string; icon: typeof Columns }[] = [
    { id: 'side-by-side', label: 'Side by Side', icon: Columns },
    { id: 'original', label: 'Original', icon: Code },
    { id: 'beautified', label: 'Beautified', icon: Eye },
  ]

  const showOriginal = viewMode === 'side-by-side' || viewMode === 'original'
  const showBeautified = viewMode === 'side-by-side' || viewMode === 'beautified'

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-white/10 bg-white/80 dark:bg-surface-900/80 flex-shrink-0">
        <div className="flex items-center gap-1">
          {VIEW_MODES.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setViewMode(id)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                viewMode === id
                  ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-400'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-white/10'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>

        {showBeautified && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPreviewMode('rendered')}
              className={`px-2 py-1 text-xs rounded-md transition-colors ${
                previewMode === 'rendered'
                  ? 'bg-gray-200 text-gray-700 dark:bg-white/10 dark:text-gray-300'
                  : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
              }`}
            >
              Preview
            </button>
            <button
              onClick={() => setPreviewMode('markdown')}
              className={`px-2 py-1 text-xs rounded-md transition-colors ${
                previewMode === 'markdown'
                  ? 'bg-gray-200 text-gray-700 dark:bg-white/10 dark:text-gray-300'
                  : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
              }`}
            >
              Markdown
            </button>
          </div>
        )}
      </div>

      {/* Panels */}
      <div className={`flex-1 min-h-0 flex ${viewMode === 'side-by-side' ? 'divide-x divide-gray-200 dark:divide-white/10' : ''}`}>
        {/* Original panel */}
        {showOriginal && (
          <div className={`${viewMode === 'side-by-side' ? 'w-1/2' : 'w-full'} overflow-auto`}>
            <div className="sticky top-0 px-4 py-1.5 bg-gray-50 dark:bg-surface-900 border-b border-gray-200 dark:border-white/10 z-10">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                Original
              </span>
              <span className="text-xs text-gray-400 ml-2">
                {originalText.split(/\s+/).filter(Boolean).length} words
              </span>
            </div>
            <div className="p-4">
              <div className="prose prose-sm dark:prose-invert max-w-none prose-headings:text-gray-900 dark:prose-headings:text-white">
                <ReactMarkdown rehypePlugins={[rehypeHighlight]}>
                  {originalText}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        )}

        {/* Beautified panel */}
        {showBeautified && (
          <div className={`${viewMode === 'side-by-side' ? 'w-1/2' : 'w-full'} overflow-auto`}>
            <div className="sticky top-0 px-4 py-1.5 bg-indigo-50 dark:bg-indigo-900/20 border-b border-gray-200 dark:border-white/10 z-10">
              <span className="text-xs font-medium text-indigo-600 dark:text-indigo-400 uppercase tracking-wide">
                Beautified
              </span>
              {beautifiedText && (
                <span className="text-xs text-gray-400 ml-2">
                  {beautifiedText.split(/\s+/).filter(Boolean).length} words
                </span>
              )}
            </div>
            <div className="p-4">
              {!beautifiedText ? (
                <div className="text-center py-12 text-gray-400">
                  <Eye className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">Beautified preview will appear here</p>
                </div>
              ) : previewMode === 'rendered' && beautifiedHtml ? (
                <div
                  className="prose prose-sm dark:prose-invert max-w-none prose-headings:text-gray-900 dark:prose-headings:text-white studio-preview"
                  dangerouslySetInnerHTML={{ __html: beautifiedHtml }}
                />
              ) : (
                <div className="prose prose-sm dark:prose-invert max-w-none prose-headings:text-gray-900 dark:prose-headings:text-white">
                  <ReactMarkdown rehypePlugins={[rehypeHighlight]}>
                    {beautifiedText}
                  </ReactMarkdown>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
